import React, { useEffect, useMemo, useRef, useState } from "react";
import useCities from "../../hooks/useCities";
import Model, { helper } from "@tripian/model";
import { api } from "@tripian/core";
import AppNav from "../../App/AppNav/AppNav";
import { CITY_INFO } from "../../constants/ROUTER_PATH_TITLE";
import { DestinationSelect, PreLoading } from "@tripian/react";
import useTranslate from "../../hooks/useTranslate";
import { useNavigate } from "react-router-dom";

export type RSelectOption = {
  id: number;
  label: string;
  payload: {
    destinationId: number;
    destinationName: string;
    coordinate: Model.Coordinate;
    parentName: string;
  };
  isSelected?: boolean;
};

const CitySelectPage = () => {
  const { cities, loadingCities } = useCities();
  const [selectedCityId, setSelectedCityId] = useState<number>();
  const [, setSearchQuery] = useState("");
  const [showAllCities, setShowAllCities] = useState(false);
  const [suggestedCities, setSuggestedCities] = useState<any[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const localIsSearchingRef = useRef(false);

  const { t } = useTranslate();

  const navigate = useNavigate();

  document.title = CITY_INFO.TITLE(t("cityInfo.title"));

  const destinations: {
    destinationId: number;
    destinationName: string;
    coordinate: Model.Coordinate;
    parentName: string;
  }[] = useMemo(() => {
    if (cities) {
      return cities?.map(city => {
        return {
          destinationId: city.id,
          destinationName: city.name,
          coordinate: city.coordinate,
          parentName:
            city.parentLocations.length === 0
              ? city.country.name
              : `${city.parentLocations.map(parent => parent.name).join(", ")}, ${city.country.name}`,
        };
      });
    }
    return [];
  }, [cities]);

  const destinationOptions: RSelectOption[] = useMemo(
    () =>
      destinations
        .sort((a, b) =>
          helper.compareStringForSort(a.destinationName, b.destinationName)
        )
        .map(d => ({
          id: d.destinationId,
          label: `${d.destinationName} - ${d.parentName}`,
          payload: d,
          isSelected: d.destinationId === selectedCityId,
        })),
    [destinations, selectedCityId]
  );

  const handleCitySelection = (selectedOption: RSelectOption) => {
    setSelectedCityId(selectedOption.id);
    navigate(`${CITY_INFO.PATH}/${selectedOption.id}`);
  };

  const handleSearchChange = (text: string, isQuickSearch = false) => {
    setSearchQuery(text);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search for very short queries
    if (text.trim().length < 2) {
      setSuggestedCities([]);
      return;
    }

    // If it's a quick search or recognized keyword, don't use debounce
    if (
      isQuickSearch ||
      ["beach", "history", "nature", "food", "culture"].includes(text)
    ) {
      performSearch(text);
      return;
    }

    // Debounce search to avoid too many API calls
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(text);
    }, 500);
  };

  const performSearch = async (text: string) => {
    try {
      // Update the ref immediately
      localIsSearchingRef.current = true;

      // Clear previous suggestions to ensure loading state is shown
      setSuggestedCities([]);

      const suggestions = await api.suggestCity(text);
      setSuggestedCities(suggestions);

      // Set searching state to false after results arrive
      localIsSearchingRef.current = false;
    } catch (error) {
      console.error("Error fetching city suggestions:", error);
      // Also set searching to false in case of error
      localIsSearchingRef.current = false;
    }
  };

  const handleSuggestedCitySelect = (cityId: number) => {
    // Find the city in the list of cities
    const city = cities?.find(city => city.id === cityId);
    if (city) {
      navigate(`/city-info/${city.id}`);
    }
  };

  // Close modal when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const popularCities = cities
    ?.filter(city => city.parentLocationId === null)
    .slice(0, 10);

  const displayedCities = showAllCities
    ? popularCities
    : popularCities?.slice(0, 3);

  return (
    <div className="min-h-scree">
      <AppNav header={CITY_INFO.HEADER?.(t("cityInfo.title"))} />
      {loadingCities ? (
        <div className="flex items-center justify-center min-h-screen">
          <PreLoading />
        </div>
      ) : (
        <div className="max-w-4xl mx-auto pt-12 px-4 pb-10">
          <h1 className="text-3xl font-semibold text-center mb-2">
            {t('cityInfo.destinationTitle')}
          </h1>
          <p className="text-center text-gray-600 mb-6">
            {t('cityInfo.destinationDesc')}
          </p>

          <div className="relative mb-10">
            <div className="relative">
              <DestinationSelect
                options={destinationOptions}
                selectedOptionId={selectedCityId}
                onSelectedOptionChange={handleCitySelection}
                placeHolder={t(
                  "trips.createNewTrip.form.destination.city.placeholder"
                )}
                showAiSuggestions={Boolean(window.tconfig.AI_SUPPORT)}
                suggestions={suggestedCities}
                onSuggestionsSearch={handleSearchChange}
                isSearching={localIsSearchingRef.current}
                onSelectSuggestedCity={handleSuggestedCitySelect}
                onClearSuggestions={() => setSuggestedCities([])}
                t={t}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">{t('cityInfo.popularCities')}</h2>
              <button
                onClick={() => setShowAllCities(!showAllCities)}
                className="text-primary-color font-medium text-base"
              >
                {showAllCities ? t('common.showLess') : t('common.showMore')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {displayedCities?.map(city => (
                <div
                  key={city.name}
                  onClick={() => navigate(`/city-info/${city.id}`)}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="relative h-48">
                    <img
                      src={city.image.url}
                      alt={city.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <h3 className="text-xl font-bold">{city.name}</h3>
                      <p className="text-white/80 text-sm">
                        {city.country.name}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySelectPage;
